import { Component, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ICouncilMappedItem } from '@app/logic/councils/interfaces/i.council.mapped';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import { CouncilLogicService } from '@app/logic/councils';
import { ToastService } from '@app/core/services/toast/toast.service';
import { CouncilMappedItem } from '@app/logic/councils/council.mapped';
import { NgForm } from '@angular/forms';
import { ICouncilLogicService } from '@app/logic/councils/interfaces/i.council.logic.service';
import { IIdAndLabelDto, ILocationDto, ILookupDto } from '@classictechsolutions/hubapi-transpiled-enums';
import { ICouncilDto } from '@app/logic/councils/interfaces/i.council.dto';
import { take } from 'rxjs';

interface IData {
    mappedItem: CouncilMappedItem;
}

/** Shown for the empty option in the picker and for an unmapped council in the grid. */
export const NOT_MAPPED_LABEL = 'Not mapped';

@Component({
    selector: 'cb-council-dialog',
    templateUrl: './council-dialog.component.html',
    styleUrls: ['./council-dialog.component.scss'],
    providers: [CouncilLogicService],
    standalone: false
})
export class CouncilDialogComponent extends
    BaseDialogFormViewDirective<ICouncilDto, ICouncilMappedItem, ICouncilLogicService> {
    @ViewChild('councilForm', { static: true }) public councilsForm: NgForm;
    public mappedItem: CouncilMappedItem;
    public selectedItem: ILookupDto;
    public selectedLocationModel: ILocationDto;

    /**
     * Active territorial authorities as select options, labelled
     * "017 - Waipa District". The code is shown alongside the name because it
     * is the value SalesIQ actually sends, and because several TA names differ
     * only by their District/City suffix.
     */
    public territorialAuthorityOptions: IIdAndLabelDto[];

    constructor(
        public readonly toastSerivce: ToastService,
        public readonly dialogRef: MatDialogRef<CouncilDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: IData,
        public readonly councilLogicService: CouncilLogicService
    ) {
        super(dialogRef, toastSerivce);
        this.mappedItem = data.mappedItem;

        this.councilLogicService.getTerritorialAuthorities().pipe(take(1)).subscribe({
            next: authorities => {
                this.territorialAuthorityOptions = [
                    // Explicit empty option: without it a council can never be
                    // un-mapped, and the API's own conflict message tells the
                    // administrator to do exactly that ("Clear it there, or
                    // deactivate that council, before mapping it here").
                    { id: null, label: NOT_MAPPED_LABEL } as IIdAndLabelDto,
                    ...(authorities ?? []).map(ta => {
                        return { id: ta.id, label: `${ta.code} - ${ta.name}` } as IIdAndLabelDto;
                    }),
                ];
            },
            // The endpoint is new, and the API deploys separately from this app,
            // so "frontend is ahead of the API" is a real state. Degrade to an
            // empty picker - cb-select then shows its no-options message - and
            // leave the rest of the dialog (name, active, areas) usable.
            error: () => {
                this.territorialAuthorityOptions = [];
            },
        });
    }

    public displayFn(item): string | undefined {
        return item ? item.label ? item.label : item.path : '';
    }

    public getDisabled(): boolean {
        return !this.councilsForm.dirty || !this.councilsForm.valid;
    }

    public itemSelected(item: ILookupDto): void {
        this.selectedItem = item;
    }
}
